import { useEffect } from 'react'
import { type QueryClient, useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { userProjectParticipationsQuery } from '@/lib/actions/getUserProjectParticipations.ts'
import { clearStageCache } from '@/lib/prefetch/clearStageCache.ts'
import { useUserStore } from '@/store/user.ts'
import { getCurrentOrganization } from '@/lib/utils.ts'

const STORAGE_PREFIX = 'participationStageIds'

/** Comparisons run one after another; see useClearVanishedStageCaches. */
let comparisons: Promise<void> = Promise.resolve()

/** Stages that were on the previous list and are not on the current one. */
export function vanishedStageIds(
  previous: readonly string[],
  current: readonly string[],
): string[] {
  const live = new Set(current)
  return previous.filter(id => !live.has(id))
}

function storageKey(userId: number, organizationId: number): string {
  return `${STORAGE_PREFIX}:${userId}:${organizationId}`
}

function readStored(key: string): string[] | null {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.map(String) : null
  } catch {
    return null
  }
}

function writeStored(key: string, ids: string[]): void {
  try {
    localStorage.setItem(key, JSON.stringify(ids))
  } catch {
    // Storage full or unavailable: the next fresh list simply has nothing to compare with.
  }
}

type ParticipationsDataT = {
  pages: ReadonlyArray<
    { projectParticipations?: ReadonlyArray<{ projectStage?: { id?: number } } | null> } | undefined
  >
}

function stageIdsOf(data: ParticipationsDataT): string[] {
  return data.pages
    .flatMap(page => page?.projectParticipations ?? [])
    .map(participation => participation?.projectStage?.id)
    .filter((id): id is number => id != null)
    .map(String)
}

/**
 * Clears each vanished stage and returns the ones whose cleanup failed, so they stay on the
 * stored list and are tried again after the next fresh fetch.
 */
async function clearVanished(queryClient: QueryClient, stageIds: string[]): Promise<string[]> {
  const failed: string[] = []
  // One at a time: clearStageCache pauses and resumes persistence around its removals.
  for (const stageId of stageIds) {
    try {
      await clearStageCache(queryClient, stageId, { stageMetadata: true })
    } catch {
      failed.push(stageId)
    }
  }
  return failed
}

/**
 * Drops the offline data of stages that left the user's stage list (D7): an archived or
 * deleted project's stage disappears from /users/me/project_participations, and the device
 * should not keep its issues and photos — the archived demo above all.
 *
 * "Left" is decided against the previous list of the SAME user and organization, not
 * against whatever happens to be cached: the list covers only the active organization, so
 * switching companies must not wipe the other company's downloads. Only a list fetched from
 * the network in this session counts as the current list.
 *
 * Known gap: the first time a user and organization are seen there is no stored list, so a
 * stage that vanished before that first fresh fetch (archived while a device was still on the
 * previous release) keeps its offline data. The persisted participations list cannot stand in:
 * rootLoader replaces it with the fresh list before this hook mounts, and its key carries no
 * organization, so a copy read under another company could wipe that company's downloads.
 */
export function useClearVanishedStageCaches(): void {
  const queryClient = useQueryClient()
  const userId = useUserStore(state => state.user?.id)
  const organizationId = getCurrentOrganization()?.organization?.id
  const { data, isFetchedAfterMount, isFetching, isSuccess, hasNextPage, dataUpdatedAt } =
    useInfiniteQuery({
      ...userProjectParticipationsQuery(),
    })

  useEffect(() => {
    // isSuccess as well: a failed refetch also counts as fetched after mount, and leaves the
    // persisted (possibly older) list in data.
    if (!isFetchedAfterMount || !isSuccess || isFetching || hasNextPage || !data) return
    if (userId == null || organizationId == null) return

    const current = stageIdsOf(data)
    const key = storageKey(userId, organizationId)
    // Queued behind any comparison still clearing: each one reads the list the previous one
    // wrote, so an older, slower cleanup cannot overwrite a newer list afterwards.
    comparisons = comparisons.then(async () => {
      const previous = readStored(key)
      const vanished = previous ? vanishedStageIds(previous, current) : []
      const failed = await clearVanished(queryClient, vanished)
      writeStored(key, [...current, ...failed])
    })
  }, [
    data,
    dataUpdatedAt,
    isFetchedAfterMount,
    isSuccess,
    isFetching,
    hasNextPage,
    userId,
    organizationId,
    queryClient,
  ])
}
